import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowRight, Braces, CheckCircle2, CircleDashed, Clock3, FlaskConical, GitCompare, History, Play, RefreshCw, ShieldCheck, XCircle } from 'lucide-react'
import { apiGet, apiPost } from '../../lib/api'
import { buildValidationRunRequest, compareValidationResults, ExternalValidationScope, groupValidationResults, isFileValidationFamily, validationRunHeadline, validationRunTypeLabel, ValidationRunMode, ValidationTestResult, ValidationTestStatus } from '../../lib/validation'
import type { ConfigurationArtifact } from './ConfigurationArtifactPanel'

interface ValidationRun {
  id: string
  status: 'passed' | 'warning' | 'failed'
  connector_family: string
  counts: Record<ValidationTestStatus, number>
  duration_ms: number
  run_mode?: ValidationRunMode
  sample_response_supplied?: boolean
  external_authorized?: boolean
  external_scope?: ExternalValidationScope | null
  created_at: string
  results?: ValidationTestResult[]
}

interface ValidationResponse {
  artifact_id: string
  artifact_version_id: string
  version: number
  latest_run: ValidationRun | null
  run_history?: ValidationRun[]
}

const statusCopy: Record<ValidationTestStatus, string> = {
  passed: 'Passed', warning: 'Warning', failed: 'Failed', not_run: 'Not run',
}

function StatusIcon({ status }: { status: ValidationTestStatus }) {
  if (status === 'passed') return <CheckCircle2 size={15} />
  if (status === 'warning') return <AlertTriangle size={15} />
  if (status === 'failed') return <XCircle size={15} />
  return <CircleDashed size={15} />
}

function hasEvidence(evidence?: Record<string, unknown>) {
  return Boolean(evidence && Object.keys(evidence).length)
}

function runOptionLabel(run: ValidationRun) {
  return `${new Date(run.created_at).toLocaleString()} · ${validationRunTypeLabel(run.run_mode, run.external_scope)}`
}

export default function ValidationTestsPanel({
  artifact, artifactLoading, onArtifactRefresh,
}: {
  artifact: ConfigurationArtifact | null
  artifactLoading: boolean
  onArtifactRefresh: () => void
}) {
  const [data, setData] = useState<ValidationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [sampleText, setSampleText] = useState('')
  const [externalConsent, setExternalConsent] = useState(false)
  const [externalScope, setExternalScope] = useState<ExternalValidationScope>('saviynt_authentication')
  const [comparisonBeforeId, setComparisonBeforeId] = useState('')
  const [comparisonAfterId, setComparisonAfterId] = useState('')
  const [comparisonRuns, setComparisonRuns] = useState<{ before: ValidationRun; after: ValidationRun } | null>(null)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [comparisonError, setComparisonError] = useState('')
  const artifactId = artifact?.id
  const artifactVersion = artifact?.version?.version
  const connectorFamily = artifact?.connector_family || ''
  const fileSample = isFileValidationFamily(connectorFamily)
  const soapSample = connectorFamily.toUpperCase() === 'SOAP'
  const runHistory = data?.run_history || []
  const runHistoryKey = runHistory.map(run => run.id).join(',')

  const load = useCallback(async () => {
    if (!artifactId || !artifactVersion) {
      setData(null)
      return
    }
    setLoading(true)
    setError('')
    try {
      setData(await apiGet(`/api/artifacts/${artifactId}/tests?version=${artifactVersion}`))
    } catch (caught: any) {
      setError(caught?.message || 'Could not load validation results')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [artifactId, artifactVersion])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (runHistory.length < 2) {
      setComparisonBeforeId('')
      setComparisonAfterId('')
      setComparisonRuns(null)
      return
    }
    setComparisonAfterId(current => runHistory.some(run => run.id === current) ? current : runHistory[0].id)
    setComparisonBeforeId(current => runHistory.some(run => run.id === current) ? current : runHistory[1].id)
  // The stable ID key prevents timestamps/count refreshes from resetting user selections.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runHistoryKey])

  useEffect(() => {
    if (!artifactId || !comparisonBeforeId || !comparisonAfterId || comparisonBeforeId === comparisonAfterId) {
      setComparisonRuns(null)
      return
    }
    let cancelled = false
    setComparisonLoading(true)
    setComparisonError('')
    Promise.all([
      apiGet(`/api/artifacts/${artifactId}/tests/runs/${comparisonBeforeId}`),
      apiGet(`/api/artifacts/${artifactId}/tests/runs/${comparisonAfterId}`),
    ]).then(([before, after]) => {
      if (!cancelled) setComparisonRuns({ before, after })
    }).catch((caught: any) => {
      if (!cancelled) {
        setComparisonRuns(null)
        setComparisonError(caught?.message || 'Could not compare validation runs')
      }
    }).finally(() => {
      if (!cancelled) setComparisonLoading(false)
    })
    return () => { cancelled = true }
  }, [artifactId, comparisonBeforeId, comparisonAfterId])

  const runChecks = async (external = false) => {
    if (!artifact?.version || running) return
    let requestBody
    try {
      requestBody = buildValidationRunRequest(sampleText, external, externalScope, connectorFamily)
    } catch (caught: any) {
      setError(caught?.message || 'The sample response must be valid for this connector family.')
      return
    }
    setRunning(true)
    setError('')
    try {
      const response = await apiPost(`/api/artifacts/${artifact.id}/versions/${artifact.version.version}/tests`, requestBody)
      setData(response)
      if (external) setExternalConsent(false)
      onArtifactRefresh()
      await load()
    } catch (caught: any) {
      setError(caught?.message || 'Validation run failed')
    } finally {
      setRunning(false)
    }
  }

  const groups = useMemo(() => {
    return groupValidationResults(data?.latest_run?.results || [])
  }, [data])
  const comparison = useMemo(() => comparisonRuns
    ? compareValidationResults(comparisonRuns.before.results || [], comparisonRuns.after.results || [])
    : null, [comparisonRuns])

  if (artifactLoading) return <div className="artifact-empty"><RefreshCw size={18} className="spin" /><p>Loading configuration version…</p></div>
  if (!artifact?.version) return (
    <div className="artifact-empty">
      <FlaskConical size={22} />
      <h3>No testable configuration yet</h3>
      <p>Generate a configuration artifact first. Validation runs are always tied to an immutable version.</p>
    </div>
  )

  const run = data?.latest_run
  return (
    <div className="validation-tests-panel">
      <div className="artifact-heading">
        <div>
          <span>Validation and tests</span>
          <h3>{artifact.connection_name || 'Unnamed connection'} · v{artifact.version.version}</h3>
        </div>
        <button type="button" onClick={load} aria-label="Refresh validation results" disabled={loading || running}>
          <RefreshCw size={13} className={loading ? 'spin' : ''} />
        </button>
      </div>

      <div className="validation-run-actions">
        <div>
          <strong>{run ? validationRunHeadline(run.status, run.run_mode) : 'No validation run yet'}</strong>
          <span>{run
            ? `${new Date(run.created_at).toLocaleString()} · ${run.duration_ms} ms · ${run.run_mode || 'local'}${run.external_scope ? ` · ${run.external_scope === 'saviynt_authentication' ? 'Saviynt authentication only' : 'full connection'}` : ''}`
            : 'Run deterministic checks without contacting external systems.'}
          </span>
        </div>
        <button type="button" className="primary" onClick={() => runChecks(false)} disabled={running}>
          {running ? <RefreshCw size={13} className="spin" /> : <Play size={13} />}
          {running ? 'Running…' : sampleText.trim() ? 'Run with sample' : run ? 'Rerun checks' : 'Run checks'}
        </button>
      </div>

      {error && <div className="validation-error" role="alert"><XCircle size={14} />{error}</div>}

      <section className="validation-input-card">
        <div className="validation-input-heading">
          <Braces size={14} />
          <div>
            <strong>{fileSample ? 'Sanitized feed sample' : 'Sanitized sample response'}</strong>
            <span>Optional · {fileSample ? 'CSV' : soapSample ? 'SOAP XML' : 'JSON'} · maximum 100 KB</span>
          </div>
        </div>
        <textarea
          aria-label="Sanitized sample response"
          placeholder={fileSample
            ? 'username|email|status\nsample.user|sample@example.test|Active'
            : soapSample
            ? '<soapenv:Envelope>…<soapenv:Body>…</soapenv:Body></soapenv:Envelope>'
            : 'Paste a small sanitized object or array, for example {"userlist":[{"id":"100"}]}'}
          value={sampleText}
          onChange={event => setSampleText(event.target.value)}
          rows={4}
          disabled={running}
        />
        <p>The submitted sample body is used in memory for this run and is not stored. Only {fileSample ? 'headers, delimiter, row counts, shape checks, and mapping evidence' : 'field names, counts, and match evidence'} are retained.</p>
      </section>

      {fileSample ? (
        <section className="validation-external-card">
          <div className="validation-input-heading">
            <ShieldCheck size={14} />
            <div><strong>Local file validation</strong><span>No external connection test</span></div>
          </div>
          <p>CSV and SAV checks run locally against sanitized structure and mapping metadata. They do not upload the file, start SchemaUserJob, or contact Saviynt.</p>
        </section>
      ) : (
        <section className="validation-external-card">
          <div className="validation-input-heading">
            <ShieldCheck size={14} />
            <div><strong>Credentialed connection test</strong><span>Explicit authorization required</span></div>
          </div>
          <label>
            Test scope
            <select
              aria-label="External test scope"
              value={externalScope}
              onChange={event => {
                setExternalScope(event.target.value as ExternalValidationScope)
                setExternalConsent(false)
              }}
              disabled={running}
            >
              <option value="saviynt_authentication">Saviynt authentication only</option>
              <option value="full_connection">Full connection test</option>
            </select>
          </label>
          <p>{externalScope === 'saviynt_authentication'
            ? 'Authenticates only to the configured Saviynt tenant. It does not call testConnection or contact the target system.'
            : <>Authenticates to Saviynt and calls testConnection with <code>saveconnection=N</code>. This may contact the configured target system but never saves or deploys the connector.</>}
          </p>
          <label>
            <input type="checkbox" checked={externalConsent} onChange={event => setExternalConsent(event.target.checked)} disabled={running} />
            {externalScope === 'saviynt_authentication'
              ? 'I authorize this version to contact the configured Saviynt tenant.'
              : 'I authorize this version to contact the configured Saviynt tenant and target system.'}
          </label>
          <button type="button" onClick={() => runChecks(true)} disabled={running || !externalConsent}>
            {running ? <RefreshCw size={13} className="spin" /> : <ShieldCheck size={13} />}
            {externalScope === 'saviynt_authentication' ? 'Test Saviynt authentication' : 'Run authorized connection test'}
          </button>
        </section>
      )}

      {run && (
        <div className="validation-counts" aria-label="Validation result counts">
          {(['passed', 'warning', 'failed', 'not_run'] as ValidationTestStatus[]).map(status => (
            <div key={status} className={status}>
              <StatusIcon status={status} />
              <strong>{run.counts?.[status] || 0}</strong>
              <span>{statusCopy[status]}</span>
            </div>
          ))}
        </div>
      )}

      {runHistory.length > 0 && (
        <section className="validation-history">
          <div className="validation-history-heading">
            <History size={14} />
            <div><strong>Validation history</strong><span>{runHistory.length} recent run{runHistory.length === 1 ? '' : 's'} for artifact v{data?.version}</span></div>
          </div>
          <ol className="validation-history-list">
            {runHistory.map((historyRun, index) => (
              <li key={historyRun.id}>
                <div>
                  <strong>{validationRunTypeLabel(historyRun.run_mode, historyRun.external_scope)}</strong>
                  <span>{new Date(historyRun.created_at).toLocaleString()} · {historyRun.duration_ms} ms</span>
                </div>
                <div className="validation-history-counts">
                  <span className="passed">{historyRun.counts?.passed || 0} passed</span>
                  {!!historyRun.counts?.failed && <span className="failed">{historyRun.counts.failed} failed</span>}
                  {!!historyRun.counts?.warning && <span className="warning">{historyRun.counts.warning} warning</span>}
                  {index === 0 && <em>Latest</em>}
                </div>
              </li>
            ))}
          </ol>

          {runHistory.length > 1 && (
            <div className="validation-comparison">
              <div className="validation-comparison-heading"><GitCompare size={13} /><strong>Compare runs</strong></div>
              <div className="validation-comparison-selectors">
                <label>
                  Earlier run
                  <select aria-label="Earlier validation run" value={comparisonBeforeId} onChange={event => setComparisonBeforeId(event.target.value)}>
                    {runHistory.map(historyRun => <option key={historyRun.id} value={historyRun.id} disabled={historyRun.id === comparisonAfterId}>{runOptionLabel(historyRun)}</option>)}
                  </select>
                </label>
                <ArrowRight size={13} />
                <label>
                  Later run
                  <select aria-label="Later validation run" value={comparisonAfterId} onChange={event => setComparisonAfterId(event.target.value)}>
                    {runHistory.map(historyRun => <option key={historyRun.id} value={historyRun.id} disabled={historyRun.id === comparisonBeforeId}>{runOptionLabel(historyRun)}</option>)}
                  </select>
                </label>
              </div>
              {comparisonLoading && <p className="validation-comparison-state"><RefreshCw size={11} className="spin" /> Loading comparison…</p>}
              {comparisonError && <p className="validation-comparison-state failed">{comparisonError}</p>}
              {comparison && !comparisonLoading && (
                <>
                  <div className="validation-comparison-summary">
                    <span className="improved">{comparison.improved} improved</span>
                    <span className="regressed">{comparison.regressed} regressed</span>
                    <span>{comparison.unchanged} unchanged</span>
                  </div>
                  {comparison.changes.length ? (
                    <div className="validation-comparison-changes">
                      {comparison.changes.map(change => (
                        <div className={change.direction} key={change.check_id}>
                          <div><strong>{change.label}</strong><span>{change.category.replaceAll('_', ' ')}</span></div>
                          <code>{statusCopy[change.before]} <ArrowRight size={10} /> {statusCopy[change.after]}</code>
                        </div>
                      ))}
                    </div>
                  ) : <p className="validation-comparison-state">No check statuses changed between these runs.</p>}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {!run && !loading ? (
        <div className="validation-empty-run">
          <FlaskConical size={20} />
          <p>Checks cover connector rules, JSON syntax, request construction, mappings, and pagination.</p>
          <span>Credentialed and network checks remain “Not run” until explicitly authorized.</span>
        </div>
      ) : groups.map(([category, results]) => (
        <section className="validation-group" key={category}>
          <h4>{category.replaceAll('_', ' ')}</h4>
          {results.map(result => (
            <div className={`validation-result ${result.status}`} key={result.check_id}>
              <StatusIcon status={result.status} />
              <div>
                <div className="validation-result-heading">
                  <strong>{result.label}</strong>
                  <span>{statusCopy[result.status]}</span>
                </div>
                {result.remediation && <p>{result.remediation}</p>}
                {hasEvidence(result.evidence) && (
                  <details>
                    <summary>Sanitized evidence</summary>
                    <pre>{JSON.stringify(result.evidence, null, 2)}</pre>
                  </details>
                )}
              </div>
            </div>
          ))}
        </section>
      ))}

      {run && (
        <div className="validation-footer">
          <Clock3 size={12} /> Tested artifact v{data?.version}; later versions require their own run.
        </div>
      )}
    </div>
  )
}
